import { useEffect, useState } from "react";

/**
 * Mount and show state for an element that animates in and out.
 * `mounted` turns on with `open` and off `exitMs` after it closes, so the exit animation can play;
 * `shown` turns on two frames after the mount commits, so the closed state paints first and the enter animation can play.
 */
export const usePresence = (open: boolean, exitMs: number): { mounted: boolean; shown: boolean } => {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(open);
  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    setShown(false);
    const timer = window.setTimeout(() => setMounted(false), exitMs);
    return () => window.clearTimeout(timer);
  }, [open, exitMs]);
  // Wait for the commit that mounts the closed state, then two frames, so a slow first mount still animates in.
  useEffect(() => {
    if (!open || !mounted) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setShown(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [open, mounted]);
  // Mounted from the very render that opens, so a parent's focus effect finds the element.
  return { mounted: open || mounted, shown };
};
